import { useState } from "react";
import { Play, AlertTriangle, Check, ChevronDown, ChevronUp, ShoppingBag, CalendarCheck } from "lucide-react";

const getVideoEmbed = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { type: "iframe", src: `https://www.youtube.com/embed/${yt[1]}` };
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return { type: "iframe", src: `https://player.vimeo.com/video/${vimeo[1]}` };
  if (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url)) return { type: "video", src: url };
  return { type: "iframe", src: url };
};

const DIFFICULTY_COLORS = {
  easy: "bg-[#e8ece6] text-[#2c4c3b]",
  medium: "bg-[#f0e9dc] text-[#8a6d3b]",
  hard: "bg-[#f5e0dc] text-[#a04a3a]",
};

export const ExerciseCard = ({ item, completed, dueToday, onToggle, t }) => {
  const [showVideo, setShowVideo] = useState(false);
  const exercise = item.exercise;
  if (!exercise) return null;
  const embed = getVideoEmbed(exercise.video_url);

  return (
    <div
      className={`bg-white rounded-3xl border overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.04)] transition-all ${
        completed ? "border-[#8a9a86]/60 bg-[#f7faf6]" : "border-stone-100"
      }`}
      data-testid={`exercise-card-${exercise.code}`}
    >
      <div className="p-5 sm:p-6">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-[11px] font-mono tracking-wide px-2.5 py-1 rounded-full bg-stone-100 text-stone-500">
            {exercise.code}
          </span>
          <span className={`text-[11px] px-2.5 py-1 rounded-full font-medium ${DIFFICULTY_COLORS[exercise.difficulty] || DIFFICULTY_COLORS.medium}`}>
            {t(`patientArea.difficulty.${exercise.difficulty}`)}
          </span>
          {dueToday && (
            <span className="text-[11px] px-2.5 py-1 rounded-full bg-[#d4c4a8] text-[#2c4c3b] font-semibold flex items-center gap-1" data-testid={`today-badge-${exercise.code}`}>
              <CalendarCheck className="w-3 h-3" /> {t("patientArea.today")}
            </span>
          )}
          {completed && (
            <span className="text-[11px] px-2.5 py-1 rounded-full bg-[#2c4c3b] text-stone-50 flex items-center gap-1">
              <Check className="w-3 h-3" /> {t("patientArea.completed")}
            </span>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          {exercise.image_url && (
            <img
              src={exercise.image_url}
              alt={exercise.title}
              className="w-full sm:w-32 h-40 sm:h-24 object-cover rounded-2xl flex-shrink-0"
            />
          )}
          <div className="flex-1">
            <h3 className="font-serif text-xl text-stone-900">{exercise.title}</h3>
            <p className="text-sm text-stone-600 leading-relaxed mt-1">{exercise.description}</p>
          </div>
        </div>

        {/* Parameters */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          {[
            [t("patientArea.sets"), item.sets],
            [t("patientArea.reps"), item.reps],
            [t("patientArea.frequency"), `${item.weekly_frequency}${t("patientArea.perWeek")}`],
            [t("patientArea.rest"), item.rest_time],
          ].map(([label, value]) => (
            <div key={label} className="bg-[#f5f2ec] rounded-2xl px-3 py-2.5 text-center">
              <p className="text-[10px] uppercase tracking-wider text-stone-500">{label}</p>
              <p className="text-sm font-semibold text-[#2c4c3b] mt-0.5">{value}</p>
            </div>
          ))}
        </div>

        {/* Mistakes */}
        {exercise.common_mistakes && (
          <div className="mt-4 bg-amber-50 border border-amber-100 rounded-2xl p-4 flex gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-amber-800">{t("patientArea.mistakes")}</p>
              <p className="text-xs text-amber-700 mt-1 leading-relaxed whitespace-pre-line">{exercise.common_mistakes}</p>
            </div>
          </div>
        )}

        {/* Equipment + Buy link */}
        {exercise.equipment_name && (
          <div className="mt-4 bg-[#f0ede4] rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-stone-500">{t("patientArea.equipmentLabel")}</p>
              <p className="text-sm font-medium text-stone-800 mt-0.5">{exercise.equipment_name}</p>
            </div>
            {exercise.equipment_url && (
              <a
                href={exercise.equipment_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full px-5 py-2.5 text-sm font-medium transition-colors flex-shrink-0"
                data-testid={`buy-equipment-${exercise.code}`}
              >
                <ShoppingBag className="w-4 h-4" />
                {t("patientArea.buy")} {exercise.equipment_name}
              </a>
            )}
          </div>
        )}

        {/* Video */}
        {embed && (
          <div className="mt-4">
            <button
              onClick={() => setShowVideo(!showVideo)}
              className="flex items-center gap-2 text-sm font-medium text-[#2c4c3b] hover:underline"
              data-testid={`toggle-video-${exercise.code}`}
            >
              <Play className="w-4 h-4" />
              {showVideo ? t("patientArea.hideVideo") : t("patientArea.watchVideo")}
              {showVideo ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showVideo && (
              <div className="mt-3 rounded-2xl overflow-hidden bg-stone-900 aspect-video">
                {embed.type === "video" ? (
                  <video src={embed.src} controls className="w-full h-full" />
                ) : (
                  <iframe
                    src={embed.src}
                    title={exercise.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* Complete toggle */}
        <button
          onClick={() => onToggle(exercise.id, !completed)}
          className={`w-full mt-5 py-3 rounded-full text-sm font-medium transition-all flex items-center justify-center gap-2 ${
            completed
              ? "bg-[#2c4c3b] text-stone-50"
              : "border border-[#2c4c3b]/30 text-[#2c4c3b] hover:bg-[#e8ece6]"
          }`}
          data-testid={`complete-btn-${exercise.code}`}
        >
          <Check className="w-4 h-4" />
          {completed ? t("patientArea.completed") : t("patientArea.markCompleted")}
        </button>
      </div>
    </div>
  );
};

export default ExerciseCard;
